import React, { useState, useEffect } from 'react'
import { User, Save, Eye, EyeOff, AlertCircle, Clock, Monitor, MapPin, Shield } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useApi } from '../../contexts/ApiContext'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'
import { trackProfileView, trackProfileUpdate } from '../../utils/eventTracking'

const CustomerProfile = () => {
  const { user, loading } = useAuth()
  const { updateTableRecord } = useApi()
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    permissions: {},
    is_active: true,
    password_hash: ''
  })
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [lastLogin, setLastLogin] = useState(null)

  console.log('CustomerProfile render - user:', user)
  console.log('CustomerProfile render - loading:', loading)
  console.log('CustomerProfile render - profileData:', profileData)

  // Track page view when user is loaded
  useEffect(() => {
    if (user && currentUserId) {
      trackProfileView(currentUserId)
    }
  }, [user, currentUserId])

  // Function to get current user's database record
  const getCurrentUserRecord = async () => {
    try {
      if (user && user.email) {
        console.log('Looking for user with email:', user.email)
        
        // Try to get user record with better error handling
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('email', user.email)
          .maybeSingle() // Use maybeSingle instead of single to avoid 406 errors
        
        if (error) {
          console.error('Error fetching user record:', error)
          // Don't return null immediately, try fallback
        } else if (data) {
          console.log('Found user record:', data)
          setCurrentUserId(data.user_id)
          return data
        } else {
          console.log('No user record found, creating fallback')
          // Create a fallback user record
          const fallbackUser = {
            user_id: user.id,
            email: user.email,
            first_name: user.user_metadata?.first_name || 'User',
            last_name: user.user_metadata?.last_name || '',
            phone: user.user_metadata?.phone || '',
            created_at: new Date().toISOString()
          }
          setCurrentUserId(user.id)
          return fallbackUser
        }
      }
    } catch (error) {
      console.error('Error in getCurrentUserRecord:', error)
      // Return fallback user data
      if (user) {
        const fallbackUser = {
          user_id: user.id,
          email: user.email,
          first_name: user.user_metadata?.first_name || 'User',
          last_name: user.user_metadata?.last_name || '',
          phone: user.user_metadata?.phone || '',
          created_at: new Date().toISOString()
        }
        setCurrentUserId(user.id)
        return fallbackUser
      }
    }
    return null
  }

  // Function to fetch user sessions
  const fetchUserSessions = async () => {
    try {
      console.log('🔍 fetchUserSessions called, user:', user?.email)
      setSessionsLoading(true)
      if (user && user.email) {
        console.log('📡 Fetching sessions for email:', user.email)
        const { data, error } = await supabase
          .from('sessions')
          .select('*')
          .eq('email', user.email)
          .order('login_time', { ascending: false })
          .limit(10) // Get last 10 sessions
        
        if (error) {
          console.error('❌ Error fetching sessions:', error)
          return
        }
        
        console.log('✅ Found sessions:', data)
        setSessions(data || [])
        
        // Set last login from the most recent session
        if (data && data.length > 0) {
          setLastLogin(data[0].login_time)
        }
      } else {
        console.log('⚠️ No user or email found for session fetch')
      }
    } catch (error) {
      console.error('❌ Error in fetchUserSessions:', error)
    } finally {
      setSessionsLoading(false)
    }
  }

  useEffect(() => {
    const loadProfileData = async () => {
      try {
        console.log('Profile component - User data:', user)
        
        if (user) {
          // First try to get the user's database record
          const userRecord = await getCurrentUserRecord()
          
          if (userRecord) {
            // Use database record data
            setProfileData({
              username: userRecord.username || '',
              email: userRecord.email || '',
              first_name: userRecord.first_name || '',
              last_name: userRecord.last_name || '',
              role: userRecord.role || '',
              permissions: userRecord.permissions || {},
              is_active: userRecord.is_active !== false,
              password_hash: ''
            })
          } else {
            // Fallback to auth user data
            setProfileData({
              username: user.username || user.user_metadata?.username || '',
              email: user.email || '',
              first_name: user.user_metadata?.first_name || '',
              last_name: user.user_metadata?.last_name || '',
              role: user.user_metadata?.role || user.role || '',
              permissions: user.user_metadata?.permissions || user.permissions || {},
              is_active: user.user_metadata?.is_active !== false,
              password_hash: ''
            })
          }
        } else {
          // Fallback values
          setProfileData({
            username: 'customer',
            email: 'customer@yourinsurance.com',
            first_name: 'Customer',
            last_name: 'User',
            role: 'customer',
            permissions: { 'view_policies': true, 'submit_claims': true },
            is_active: true,
            password_hash: ''
          })
        }
        setIsLoading(false)
        
        // Also fetch user sessions
        await fetchUserSessions()
      } catch (error) {
        console.error('Error loading profile data:', error)
        setIsLoading(false)
      }
    }

    loadProfileData()
  }, [user])

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Filter out fields that shouldn't be updated
      const updateData = { ...profileData }
      
      // Only update if password was provided
      if (profileData.password_hash && profileData.password_hash.trim() !== '') {
        // Password was provided, keep it
      } else {
        delete updateData.password_hash
      }

      // Update user profile
      if (currentUserId) {
        console.log('Updating user with ID:', currentUserId)
        console.log('Update data:', updateData)
        await updateTableRecord('users', currentUserId, updateData, 'user_id')
        
        toast.success('Profile updated successfully!')
        setIsEditing(false)
        
        // Update local storage with new data
        const updatedUser = {
          ...user,
          username: updateData.username,
          email: updateData.email,
          user_metadata: {
            ...user?.user_metadata,
            first_name: updateData.first_name,
            last_name: updateData.last_name,
            role: updateData.role,
            permissions: updateData.permissions,
            is_active: updateData.is_active
          }
        }
        localStorage.setItem('user', JSON.stringify(updatedUser))
      } else {
        toast.error('User record not found in database. Please contact administrator.')
      }
      
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    // Reset to original data
    setProfileData({
      username: user?.username || '',
      email: user?.email || '',
      first_name: user?.user_metadata?.first_name || '',
      last_name: user?.user_metadata?.last_name || '',
      role: user?.user_metadata?.role || '',
      permissions: user?.user_metadata?.permissions || {},
      is_active: user?.user_metadata?.is_active !== false,
      password_hash: ''
    })
    setIsEditing(false)
    setShowPassword(false)
  }

  const renderPermissions = (permissions) => {
    if (!permissions || typeof permissions !== 'object') {
      return 'No permissions'
    }
    
    const activePermissions = Object.keys(permissions).filter(key => permissions[key] === true)
    
    if (activePermissions.length === 0) {
      return 'No permissions'
    }
    
    return activePermissions.map(perm => 
      perm.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    ).join(', ')
  }

  const formatSessionDuration = (loginTime, logoutTime) => {
    if (!logoutTime) return 'Active'
    
    const login = new Date(loginTime)
    const logout = new Date(logoutTime)
    const diffMs = logout - login
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`
    if (diffHours > 0) return `${diffHours}h ${diffMinutes % 60}m`
    return `${diffMinutes}m`
  }

  const getSessionStatus = (session) => {
    // Use session_type instead of is_active
    if (session.session_type === 'login' && !session.logout_time) return { text: 'Active', color: 'bg-green-100 text-green-800' }
    if (session.session_type === 'logout' || session.logout_reason === 'user_logout') return { text: 'Logged Out', color: 'bg-gray-100 text-gray-800' }
    if (session.logout_reason === 'timeout') return { text: 'Timeout', color: 'bg-yellow-100 text-yellow-800' }
    return { text: 'Ended', color: 'bg-red-100 text-red-800' }
  }

  if (loading || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading profile...</span>
      </div>
    )
  }

  // Ensure we have valid profile data
  if (!profileData || Object.keys(profileData).length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Not Found</h3>
          <p className="text-gray-500">Unable to load profile data. Please try refreshing the page.</p>
        </div>
      </div>
    )
  }

  // Add error boundary for user object
  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Error</h3>
          <p className="text-gray-500">User not authenticated. Please log in again.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Profile</h1>
        <p className="text-sm sm:text-base text-gray-600">View and edit your profile information</p>
      </div>

      {/* Profile Information Card */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {profileData.first_name} {profileData.last_name}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 truncate">{profileData.email}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-primary flex items-center gap-2 text-sm sm:text-base px-3 sm:px-4 whitespace-nowrap"
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Edit Profile</span>
                <span className="sm:hidden">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="btn-secondary text-sm sm:text-base px-3 sm:px-4"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="btn-primary flex items-center gap-2 text-sm sm:text-base px-3 sm:px-4"
                >
                  <Save className="h-4 w-4" />
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            {isEditing ? (
              <input
                type="text"
                value={profileData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className="input-field"
                placeholder="Enter username"
              />
            ) : (
              <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
                {profileData.username || 'Not set'}
              </div>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            {isEditing ? (
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input-field"
                placeholder="Enter email"
              />
            ) : (
              <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
                {profileData.email || 'Not set'}
              </div>
            )}
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            {isEditing ? (
              <input
                type="text"
                value={profileData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className="input-field"
                placeholder="Enter first name"
              />
            ) : (
              <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
                {profileData.first_name || 'Not set'}
              </div>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            {isEditing ? (
              <input
                type="text"
                value={profileData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className="input-field"
                placeholder="Enter last name"
              />
            ) : (
              <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
                {profileData.last_name || 'Not set'}
              </div>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
              {profileData.role ? profileData.role : 'Not set'}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                profileData.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {profileData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Password */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            {isEditing ? (
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={profileData.password_hash}
                  onChange={(e) => handleInputChange('password_hash', e.target.value)}
                  className="input-field pr-10"
                  placeholder="Enter new password (leave blank to keep current)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
                ••••••••
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Session Details Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Session Details</h3>
          <button
            onClick={fetchUserSessions}
            disabled={sessionsLoading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {sessionsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        
        {sessionsLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading sessions...</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <Shield className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>No session history found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session, index) => {
              const status = getSessionStatus(session)
              const duration = formatSessionDuration(session.login_time, session.logout_time)
              
              return (
                <div key={session.session_id || index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Monitor className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">
                        {session.device_type?.charAt(0).toUpperCase() + session.device_type?.slice(1) || 'Unknown Device'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        {status.text}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{duration}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-3 w-3" />
                      <span>Login: {new Date(session.login_time).toLocaleString()}</span>
                    </div>
                    {session.logout_time && (
                      <div className="flex items-center space-x-2">
                        <Clock className="h-3 w-3" />
                        <span>Logout: {new Date(session.logout_time).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Monitor className="h-3 w-3" />
                      <span>{session.browser} on {session.os}</span>
                    </div>
                    {session.ip_address && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-3 w-3" />
                        <span>IP: {session.ip_address}</span>
                      </div>
                    )}
                    {session.location_city && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-3 w-3" />
                        <span>City: {session.location_city}, {session.location_state || session.location_country}</span>
                      </div>
                    )}
                    {session.location_latitude && session.location_longitude && (
                      <div className="flex items-center space-x-2 col-span-2">
                        <MapPin className="h-3 w-3" />
                        <span>Coordinates: {parseFloat(session.location_latitude).toFixed(5)}, {parseFloat(session.location_longitude).toFixed(5)}</span>
                      </div>
                    )}
                  </div>
                  
                  {session.user_agent && (
                    <div className="mt-2 text-xs text-gray-500 break-all" title={session.user_agent}>
                      {session.user_agent}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Account Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Permissions */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
          <div className="text-sm text-gray-900 p-3 bg-gray-50 rounded">
            {renderPermissions(profileData.permissions)}
          </div>
        </div>

        {/* Account Info */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-500">User ID:</span>
              <p className="text-sm text-gray-900 font-mono">{user?.id || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Last Login:</span>
              <p className="text-sm text-gray-900">
                {lastLogin ? new Date(lastLogin).toLocaleString() : 
                 user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 
                 'Never'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="card bg-yellow-50 border-yellow-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-yellow-800">Important Notes</h4>
            <ul className="text-sm text-yellow-700 mt-2 space-y-1">
              <li>• Changes take effect immediately</li>
              <li>• Password changes require re-login</li>
              <li>• Contact support for permission changes</li>
              <li>• Session history shows your recent logins</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerProfile